import React, { createContext, useContext, useState, useEffect } from 'react';
import { buildApiUrl } from './config';

const PoliticianDataContext = createContext();

export const PoliticianDataProvider = ({ children }) => {
  const [politicians, setPoliticians] = useState([]);
  const [politicianUpdates, setPoliticianUpdates] = useState({});
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tagLoading, setTagLoading] = useState(false);

  useEffect(() => {
    fetchPoliticians();
    fetchPolicyTags();
  }, []);

  const fetchPoliticians = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(buildApiUrl('/api/politicians'));
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setPoliticians(data);
      fetchAllPoliticianUpdates(data);
    } catch (err) {
      setError('Failed to load politicians.');
      setPoliticians([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPoliticianUpdates = async (politiciansList) => {
    try {
      const updatePromises = politiciansList.map(politician => fetchPoliticianUpdates(politician.id, true));
      await Promise.all(updatePromises);
    } catch (error) {
      // ignore
    }
  };

  const fetchPoliticianUpdates = async (politicianId, skipLoading = false) => {
    try {
      if (!skipLoading) setLoading(true);
      const response = await fetch(buildApiUrl(`/api/politicians/${politicianId}/updates`));
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const updates = await response.json();
      setPoliticianUpdates(prev => ({ ...prev, [politicianId]: updates }));
    } catch (err) {
      setPoliticianUpdates(prev => ({ ...prev, [politicianId]: [] }));
    } finally {
      if (!skipLoading) setLoading(false);
    }
  };

  const fetchPolicyTags = async () => {
    try {
      setTagLoading(true);
      const response = await fetch(buildApiUrl('/api/policy-tags'));
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const allTags = await response.json();
      const universalTags = allTags.filter(tag => tag.scope === 'universal');
      setAvailableTags(universalTags);
    } catch (err) {
      setAvailableTags([]);
    } finally {
      setTagLoading(false);
    }
  };

  return (
    <PoliticianDataContext.Provider value={{ politicians, politicianUpdates, availableTags, loading, error, tagLoading }}>
      {children}
    </PoliticianDataContext.Provider>
  );
};

export const usePoliticianData = () => useContext(PoliticianDataContext); 